import { useEffect } from 'react';
import Header from '../home/components/Header';
import ProductNav from '../../components/feature/ProductNav';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

export default function GenerationalWealth() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Generational Wealth Building | Life Insurance & Legacy Planning Indiana';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Build lasting generational wealth with strategic life insurance and legacy planning. Protect your family\'s financial future across generations in Indiana.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'generational wealth, legacy planning, wealth transfer, life insurance estate planning, family wealth protection');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/generational-wealth`);

    // Schema.org JSON-LD - Service
    const serviceSchema = document.createElement('script');
    serviceSchema.id = 'schema-generational-wealth-service';
    serviceSchema.type = 'application/ld+json';
    serviceSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Generational Wealth Building Services",
      "description": "Strategic life insurance and legacy planning to build and protect generational wealth for Indiana families",
      "provider": {
        "@type": "Organization",
        "name": "Hoosier Life Insurance",
        "url": SITE_URL
      },
      "areaServed": {
        "@type": "State",
        "name": "Indiana"
      },
      "serviceType": "Generational Wealth Planning"
    });
    document.head.appendChild(serviceSchema);

    // Schema.org JSON-LD - BreadcrumbList
    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.id = 'schema-generational-wealth-breadcrumb';
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Generational Wealth",
          "item": `${SITE_URL}/generational-wealth`
        }
      ]
    });
    document.head.appendChild(breadcrumbSchema);

    // Cleanup function
    return () => {
      const schemas = [
        'schema-generational-wealth-service',
        'schema-generational-wealth-breadcrumb'
      ];
      schemas.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [SITE_URL]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/generational-wealth#webpage`,
        url: `${SITE_URL}/generational-wealth`,
        name: 'Building Generational Wealth with Life Insurance in Indiana',
        description: 'Learn how life insurance, whole life policies, annuities, and estate planning create lasting wealth for Indiana families. Expert guidance on wealth transfer strategies.',
        inLanguage: 'en-US',
        datePublished: '2025-01-15T00:00:00-05:00',
        dateModified: new Date().toISOString(),
        breadcrumb: {
          '@id': `${SITE_URL}/generational-wealth#breadcrumb`
        },
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/generational-wealth#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_URL}/`
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Generational Wealth',
            item: `${SITE_URL}/generational-wealth`
          }
        ]
      },
      {
        '@type': 'Article',
        '@id': `${SITE_URL}/generational-wealth#article`,
        headline: 'Building Generational Wealth with Life Insurance in Indiana',
        description: 'Learn how life insurance, whole life policies, annuities, and estate planning create lasting wealth for Indiana families. Expert guidance on wealth transfer strategies.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright',
          jobTitle: 'Insurance Specialist',
          worksFor: {
            '@type': 'Organization',
            name: 'Hoosier Life Insurance',
            url: SITE_URL
          }
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo.png`
          }
        },
        datePublished: '2025-01-15T00:00:00-05:00',
        dateModified: new Date().toISOString(),
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/generational-wealth`
        },
        articleSection: 'Financial Planning',
        keywords: 'generational wealth, life insurance Indiana, wealth transfer, whole life insurance, estate planning, annuities, financial legacy'
      },
      {
        '@type': 'InsuranceAgency',
        '@id': `${SITE_URL}/#organization`,
        name: 'Hoosier Life Insurance',
        url: SITE_URL,
        telephone: '+13176482983',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Indianapolis',
          addressRegion: 'IN',
          addressCountry: 'US'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
          worstRating: '1'
        },
        review: [
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Marcus T.'
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5'
            },
            datePublished: '2025-02-10',
            reviewBody: 'Benjamin helped me understand how to use whole life insurance as the foundation of my family\'s wealth plan. My kids will have a financial head start I never had. Incredibly knowledgeable and patient throughout the entire process.'
          },
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Denise R.'
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5'
            },
            datePublished: '2025-03-05',
            reviewBody: 'I came in knowing nothing about generational wealth and left with a complete strategy — term life for income protection, whole life for cash value, and an annuity for retirement. Hoosier Life Insurance made it all make sense.'
          },
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Jerome & Alicia W.'
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5'
            },
            datePublished: '2025-04-18',
            reviewBody: 'We wanted to leave something meaningful for our grandchildren. Benjamin walked us through estate planning, beneficiary setup, and the right policy mix. We finally feel like we have a real legacy plan in place.'
          },
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Tanya M.'
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5'
            },
            datePublished: '2025-05-02',
            reviewBody: 'As a single mom in Indianapolis, I was worried I couldn\'t afford to build generational wealth. Benjamin showed me an affordable whole life policy that builds cash value and leaves my daughter a tax-free inheritance. Game changer.'
          },
          {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: 'Robert K.'
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5',
              bestRating: '5'
            },
            datePublished: '2025-05-20',
            reviewBody: 'The wealth strategy call was eye-opening. I had no idea how much I was leaving on the table without a proper legacy plan. Hoosier Life Insurance gave me a clear roadmap and I\'m already seeing the benefits of the cash value growth.'
          }
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/generational-wealth#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is generational wealth and how do I build it?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Generational wealth is financial assets passed down from one generation to the next, creating lasting security for your family. You build it through life insurance death benefits, cash value accumulation in whole life policies, real estate, investments, and proper estate planning. Life insurance is the foundation because it guarantees wealth transfer regardless of when you pass away.'
            }
          },
          {
            '@type': 'Question',
            name: 'Why is life insurance the foundation of generational wealth?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Life insurance creates instant wealth the day you die, regardless of how much you contributed. A $500,000 policy might cost $50/month but delivers half a million tax-free to your family. It protects against premature death, bypasses probate, and provides liquidity for estate taxes and final expenses. No other financial tool guarantees wealth transfer this efficiently.'
            }
          },
          {
            '@type': 'Question',
            name: 'Should I use whole life or term life insurance for wealth building?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Use both strategically. Term life insurance provides maximum death benefit coverage during your working years when your family depends on your income. Whole life insurance builds cash value you can borrow against, acts as a forced savings account, and provides permanent coverage for estate planning and final expenses. Most families need term for income replacement and whole life for wealth accumulation.'
            }
          },
          {
            '@type': 'Question',
            name: 'How do annuities fit into a generational wealth strategy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Annuities provide guaranteed retirement income you cannot outlive, protecting your other assets from being depleted. This preserves your life insurance death benefit, home equity, and investments for your heirs. Fixed indexed annuities offer growth potential with downside protection, while immediate annuities convert a lump sum into lifetime income. They complement life insurance by ensuring you do not drain your estate during retirement.'
            }
          },
          {
            '@type': 'Question',
            name: 'What estate planning steps do I need for wealth transfer?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Essential steps include naming beneficiaries on all life insurance policies and retirement accounts, creating a will or trust to direct asset distribution, establishing power of attorney for financial and healthcare decisions, and coordinating with a tax professional to minimize estate taxes. Life insurance proceeds are tax-free to beneficiaries and bypass probate when beneficiaries are named correctly.'
            }
          },
          {
            '@type': 'Question',
            name: 'How much life insurance do I need to leave a legacy in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Start with 10-12 times your annual income for income replacement, then add coverage for specific goals: mortgage payoff, college funding, final expenses, and legacy gifts. For example, if you earn $75,000 annually, want to leave $200,000 for your children\'s education, and have a $250,000 mortgage, you need at least $1.2 million in coverage. We help Indiana families calculate the right amount based on their unique goals.'
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <a href="/" className="text-gray-600 hover:text-orange-600 transition-colors">
                Home
              </a>
            </li>
            <li className="flex items-center">
              <i className="ri-arrow-right-s-line text-gray-400 text-lg"></i>
              <span className="text-orange-600 font-medium ml-2">Generational Wealth</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Build Generational Wealth — Life Insurance Strategies for Indiana Families
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 leading-relaxed">
              Secure your family's financial future for generations. Learn how life insurance, whole life policies, and annuities create lasting wealth for Indiana families. Discover proven strategies to protect your legacy, build cash value, and ensure your children and grandchildren have the financial foundation you never had.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/wealth-strategy-call"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                aria-label="Book a free wealth strategy call with Hoosier Life Insurance"
              >
                Book Free Strategy Call
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </Link>
              <Link
                to="/book-appointment"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                aria-label="Get your free life insurance quote"
              >
                Get Your Free Quote
                <i className="ri-calculator-line ml-2 text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Generational Wealth */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                What is Generational Wealth?
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Generational wealth is financial assets and resources passed down from one generation to the next, creating lasting security and opportunity for your children, grandchildren, and beyond.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                It's not just about leaving money behind — it's about building a foundation that allows your family to thrive without the financial stress you may have experienced. Generational wealth provides:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Financial Security</strong>
                    <span className="text-gray-700">Protection against unexpected loss of income or medical emergencies</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Educational Opportunities</strong>
                    <span className="text-gray-700">College funding without student loan debt for your children</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Business & Homeownership</strong>
                    <span className="text-gray-700">Capital for starting businesses or purchasing first homes</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Retirement Confidence</strong>
                    <span className="text-gray-700">Knowing you won't burden your children in your later years</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/7524a74c36eac3f71c365f5531eedb06.jpg"
                alt="Multi-generational Indiana family planning financial legacy together"
                title="Generational Wealth Planning with Life Insurance — Indiana Families Building a Legacy"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="w-full h-auto rounded-lg shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Life Insurance is the Foundation */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Life Insurance is the Foundation of Generational Wealth
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Life insurance is the only financial tool that creates instant, guaranteed wealth the day you pass away — regardless of how much you've contributed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Instant Wealth Creation</h3>
              <p className="text-gray-700 leading-relaxed">
                A $500,000 policy might cost $50/month but delivers half a million dollars tax-free to your family. No other investment guarantees this return regardless of when you die.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-time-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Protects Against Premature Death</h3>
              <p className="text-gray-700 leading-relaxed">
                If you pass away before accumulating wealth through savings or investments, life insurance ensures your family still receives the financial legacy you intended.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Tax-Free Wealth Transfer</h3>
              <p className="text-gray-700 leading-relaxed">
                Life insurance death benefits pass to beneficiaries 100% tax-free and bypass probate, meaning your family gets the full amount quickly without legal delays or tax erosion.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Provides Estate Liquidity</h3>
              <p className="text-gray-700 leading-relaxed">
                Life insurance provides immediate cash to pay estate taxes, final expenses, and debts without forcing your heirs to sell the family home or business.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-scales-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Equalizes Inheritance</h3>
              <p className="text-gray-700 leading-relaxed">
                If you plan to leave a business or property to one child, life insurance can provide equal value to other children, preventing family disputes over inheritance.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Creditor Protection</h3>
              <p className="text-gray-700 leading-relaxed">
                In Indiana, life insurance cash value and death benefits are protected from creditors, lawsuits, and bankruptcy, ensuring your wealth reaches your family.
              </p>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg">
            <p className="text-lg text-gray-800 leading-relaxed">
              <strong className="text-slate-900">Bottom line:</strong> Life insurance is the only financial tool that guarantees wealth transfer regardless of market performance, economic conditions, or how long you live. It's the foundation every generational wealth strategy should be built on.
            </p>
          </div>
        </div>
      </section>

      {/* Whole Life vs Term Life */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Whole Life vs. Term Life: Which Builds Generational Wealth?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              The answer is both — but they serve different purposes in your wealth-building strategy.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Term Life */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-lg p-8 border-2 border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center">
                  <i className="ri-calendar-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 ml-4">Term Life Insurance</h3>
              </div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <strong>Best for:</strong> Maximum death benefit coverage during your working years when your family depends on your income.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Affordable premiums — 10-15x cheaper than whole life for the same death benefit</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Covers specific time periods (10, 20, 30 years) when your family needs protection most</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Ideal for income replacement, mortgage protection, and college funding</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">No cash value accumulation — purely death benefit protection</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Coverage expires after the term ends (though convertible to whole life)</span>
                </li>
              </ul>
              <Link
                to="/term-life"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap"
              >
                Learn More About Term Life
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            {/* Whole Life */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-8 border-2 border-orange-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-orange-600 flex items-center justify-center">
                  <i className="ri-infinity-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 ml-4">Whole Life Insurance</h3>
              </div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                <strong>Best for:</strong> Cash value accumulation, permanent coverage, and long-term wealth building.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Builds cash value you can borrow against tax-free for emergencies or opportunities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Permanent coverage for life — never expires as long as premiums are paid</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Acts as a forced savings account with guaranteed growth</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Ideal for estate planning, final expenses, and leaving a guaranteed legacy to your heirs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-line text-red-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="ml-3 text-gray-700">Higher premiums — typically 10-15x more expensive than term life</span>
                </li>
              </ul>
              <Link
                to="/whole-life"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
              >
                Learn More About Whole Life
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>

          <div className="bg-slate-900 text-white rounded-lg p-8">
            <h3 className="text-2xl font-bold mb-4">The Optimal Strategy: Use Both</h3>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Most Indiana families benefit from a combination approach:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-xl font-bold mb-3 text-orange-400">Term Life (Ages 30-60)</h4>
                <p className="text-gray-300 leading-relaxed">
                  Provides maximum death benefit coverage during your peak earning years when your family depends on your income. Covers mortgage, college, and income replacement.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-xl font-bold mb-3 text-orange-400">Whole Life (Lifelong)</h4>
                <p className="text-gray-300 leading-relaxed">
                  Builds cash value you can access during your lifetime and provides permanent coverage for final expenses, estate taxes, and leaving a guaranteed legacy to your heirs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Annuities for Retirement Income */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=Happy%20retired%20couple%20in%20their%20early%2060s%20relaxing%20on%20a%20wooden%20porch%20swing%20at%20a%20peaceful%20Indiana%20countryside%20home%20surrounded%20by%20green%20trees%20and%20open%20fields%20during%20golden%20hour%20sunset%20warm%20amber%20light%20content%20expressions%20holding%20hands%20representing%20financial%20freedom%20and%20retirement%20security%20professional%20lifestyle%20photography%20shallow%20depth%20of%20field&width=600&height=500&seq=gw-retirement-couple-2025b&orientation=landscape"
                alt="Retired Indiana couple enjoying financial security in retirement"
                title="Retirement Financial Security through Life Insurance and Annuities — Indiana"
                loading="lazy"
                decoding="async"
                width={600}
                height={500}
                className="w-full h-auto rounded-lg shadow-2xl object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                How Annuities Preserve Generational Wealth
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Annuities provide guaranteed retirement income you cannot outlive, which protects your other assets from being depleted during your lifetime. This ensures your life insurance death benefit, home equity, and investments remain intact for your heirs.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Guaranteed Lifetime Income</strong>
                    <span className="text-gray-700">Receive monthly payments for life, eliminating the risk of outliving your savings</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Protects Principal for Heirs</strong>
                    <span className="text-gray-700">By covering living expenses with annuity income, you preserve other assets for inheritance</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Tax-Deferred Growth</strong>
                    <span className="text-gray-700">Earnings grow tax-deferred until withdrawal, maximizing accumulation</span>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-orange-600 text-lg"></i>
                  </div>
                  <div className="ml-4">
                    <strong className="text-slate-900 block mb-1">Downside Protection</strong>
                    <span className="text-gray-700">Fixed indexed annuities offer growth potential with protection against market losses</span>
                  </div>
                </div>
              </div>
              <Link
                to="/annuities"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Explore Annuity Options
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Estate Planning Basics */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Estate Planning Essentials for Wealth Transfer
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Life insurance is only effective if your estate plan is properly structured. Here's what every Indiana family needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-user-heart-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Name Beneficiaries Correctly</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Always name primary and contingent beneficiaries on all life insurance policies and retirement accounts. This ensures assets bypass probate and go directly to your heirs. Review and update beneficiaries after major life events (marriage, divorce, births).
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-file-text-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Create a Will or Trust</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                A will directs how your assets are distributed and names guardians for minor children. A trust can avoid probate, provide privacy, and control how and when heirs receive assets. Work with an Indiana estate attorney to determine which is right for you.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-hand-heart-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Establish Power of Attorney</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Designate someone to make financial and healthcare decisions if you become incapacitated. Without this, your family may need to go to court to manage your affairs, causing delays and expense during an already difficult time.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-calculator-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Minimize Estate Taxes</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                While Indiana has no state estate tax, federal estate tax applies to estates over $13.61 million (2024). Life insurance death benefits are tax-free to beneficiaries but count toward your estate value. Coordinate with a tax professional for large estates.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Coordinate All Policies</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Ensure your life insurance, retirement accounts, will, and trust all work together. Conflicting beneficiary designations or outdated documents can cause family disputes and unintended consequences. Review your entire estate plan every 3-5 years.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 border-2 border-gray-200 hover:border-orange-400 transition-colors">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-discuss-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 ml-3">Communicate Your Plan</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Tell your family where important documents are stored and explain your wishes. This prevents confusion and conflict after you're gone. Consider a family meeting to discuss your estate plan openly and answer questions.
              </p>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg">
            <p className="text-lg text-gray-800 leading-relaxed">
              <strong className="text-slate-900">Important:</strong> We are life insurance professionals, not estate attorneys. We can help you understand how life insurance fits into your estate plan, but you should work with a qualified Indiana estate planning attorney to create wills, trusts, and other legal documents.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Build Generational Wealth for Your Indiana Family?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Schedule a free wealth strategy call to discuss your goals, review your current coverage, and create a customized plan for protecting and growing your family's financial legacy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/wealth-strategy-call"
              className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Free Strategy Call
              <i className="ri-calendar-check-line ml-2 text-xl"></i>
            </Link>
            <div className="text-center">
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 bg-[#D97642] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#C26638] transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Building Blocks of Generational Wealth */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              The Building Blocks of Generational Wealth
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Every successful wealth transfer strategy uses these core financial products. Here's how they work together to protect and grow your family's legacy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Link
              to="/whole-life"
              className="group bg-gray-50 rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-heart-pulse-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Whole Life Insurance
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  The foundation: guaranteed growth that never goes backward. Builds cash value you can borrow against while maintaining your death benefit protection.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore Whole Life <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/wealth-building"
              className="group bg-gray-50 rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-funds-box-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  The 6-Step Wealth-Building System
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Our proven blueprint for tax-free retirement income and generational wealth transfer. See exactly how each product fits into your strategy.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore the System <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/annuities"
              className="group bg-gray-50 rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-line-chart-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Fixed Indexed Annuities
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Grow your money with zero market risk. Tax-advantaged growth that protects your principal while preserving other assets for your heirs.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore Annuities <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/indexed-universal-life"
              className="group bg-gray-50 rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-funds-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Indexed Universal Life
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Flexible planning with market-linked growth potential. Adjust premiums as your income changes while building tax-deferred wealth.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore IUL <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <h4 className="text-lg font-bold text-slate-900 mb-2">Find Out Which Product Is Right for You</h4>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Not sure where to start? Take our quick quiz to get a personalized recommendation based on your age, income, and financial goals.
                </p>
                <Link
                  to="/quiz"
                  className="inline-flex items-center justify-center px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  <i className="ri-questionnaire-line mr-2 text-lg"></i>
                  Take the Free Quiz
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions About Generational Wealth
            </h2>
            <p className="text-xl text-gray-700">
              Common questions Indiana families ask about building lasting financial legacies.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">What is generational wealth and how do I build it?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Generational wealth is financial assets passed down from one generation to the next, creating lasting security and opportunity for your children, grandchildren, and beyond. The most effective tools for building it in Indiana are life insurance (for immediate, guaranteed wealth transfer), annuities (for retirement income that preserves other assets), and whole life or IUL policies (for tax-deferred cash value accumulation). The key is starting early and being consistent — even a $50/month whole life policy started at age 30 builds meaningful cash value by the time your children inherit it.</p>
            </div>

            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">Why is life insurance the foundation of generational wealth?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Life insurance creates instant wealth the day you die, regardless of how much you contributed. A $500,000 policy might cost $50/month but delivers half a million dollars tax-free to your family — no other investment guarantees that return on day one. For Indiana families, there is no state income tax on life insurance death benefits, making the full death benefit available to heirs without state tax erosion. It also bypasses probate, meaning your family receives the money quickly without court delays or legal fees.</p>
            </div>

            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">Should I use whole life or term life insurance for wealth building?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Use both strategically. Term life provides maximum death benefit coverage during your working years (ages 30–60) when your family depends on your income — it's affordable and gives you the biggest death benefit per dollar of premium. Whole life builds guaranteed cash value you can borrow against, acts as a forced savings account, and provides permanent coverage for estate planning and final expenses. Most Indiana families we work with carry both: a large term policy for income replacement and a smaller whole life policy for long-term wealth accumulation and legacy transfer.</p>
            </div>

            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">How do annuities fit into a generational wealth strategy?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Annuities provide guaranteed retirement income you cannot outlive, which protects your other assets from being depleted during your lifetime. This ensures your life insurance death benefit, home equity, and investments remain intact for your heirs. Fixed indexed annuities also offer principal protection — your balance never decreases due to market losses — making them an ideal complement to market-based retirement accounts.</p>
            </div>

            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">What estate planning steps do I need for wealth transfer in Indiana?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Essential steps: name primary and contingent beneficiaries on all life insurance policies and retirement accounts — this bypasses probate entirely. Create a will or trust to direct asset distribution and name guardians for minor children. Establish durable power of attorney for financial and healthcare decisions. Indiana has no state estate tax, but federal estate tax applies to estates over $13.61 million (2024). Coordinate your life insurance with a qualified Indiana estate planning attorney to ensure your policies, will, and trust all work together as intended.</p>
            </div>

            <div className="bg-white rounded-xl p-7 border border-gray-200">
              <h3 className="font-bold text-lg text-slate-900 mb-3">How much life insurance do I need to leave a legacy in Indiana?</h3>
              <p className="text-gray-700 leading-relaxed text-sm">Start with 10–12 times your annual income as a baseline for income replacement, then layer in additional coverage for specific legacy goals: mortgage payoff, college funding for children or grandchildren, final expense coverage, and any legacy gifts to charity or heirs. For example, a 40-year-old earning $80,000/year with a $300,000 mortgage and two children in college might need $1.2M–$1.5M total. A free wealth strategy consultation with our Indiana team can run a personalized needs analysis in under 30 minutes.</p>
            </div>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Generational Wealth Life Insurance Indiana" />
      <Footer />
    </div>
  );
}